/* eslint-disable max-lines */
import { ClientFilterObject, WhereFilter } from '@common/filter.decorator';
import { Component, Input, ViewChild, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

import { IonContent } from '@ionic/angular';

import { filter, lastValueFrom } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { BaseListFilterPage, CONTAIN_FILTER_SEPARATOR } from 'src/app/@shared/base-list-filter-page';

import { ArrayKeys, TopicForm } from 'src/app/@shared/form/form.types';
import { MULTI_FIELD_SEARCH_KEY } from '@common/search-filter.constants';
import { NavigationEnd } from '@angular/router';
import { ORGANIZATION_LIST_ROUTE } from 'src/app/organization/@shared/organization-routes';
import { TOPIC_FILTER_FORM } from 'src/app/@shared/form/form-config';
import { TopicModel, TopicSource } from 'src/app/topic/@shared/topic.model';
import { TopicService } from 'src/app/topic/@shared/topic.service';
import { getKeysByFormConfig } from 'src/app/@shared/form/form-helper';
import { isPublicWebAppBuild } from 'src/app/@shared/is-public-web-app';

const SKELETON_ITEM_COUNT = 30;
const HTTP_UNAUTHORIZED = 401;
const PUBLIC_TOPIC_FILTER_KEYS = ['isRareDisease'];

const FORM_FILTER_KEYS = getKeysByFormConfig(TOPIC_FILTER_FORM);
// !!! IMPORTANT: we have to list all search/filter keys here which have array values !!!
const ARRAY_VALUES: ArrayKeys<TopicSource>[] = [
  'area',
  'domain',
  'icdArea',
  'icdGroup',
  'orphanetSynonyms',
  'sector',
  'synonyms',
];

@Component({
  selector: 'page-topic-list',
  standalone: false,
  styleUrls: ['./topic-list.page.scss'],
  templateUrl: './topic-list.page.html',
})
export class TopicListPage extends BaseListFilterPage<TopicModel, TopicSource> {
  @ViewChild('ListContent', {
    static: false,
  }) public content: IonContent;

  @Input() public isMultiSelect: boolean;
  @Input() public selectedItems: TopicModel[] = [];
  @Input() public enableAllSelection: boolean = false;
  @Input() public selectedAllState: { excludedIds: string[]; isAllSelected: boolean; whereFilter?: WhereFilter<TopicSource> } | null = null;

  public readonly pageTrackName = 'TopicListPage';
  public readonly pageTrackTitle = 'Themensuche | NAKOS Adresswelt';

  public path = 'topics';

  public items: { [key: string]: TopicModel[] } = {};
  public currentListType: 'thumbnail' | 'card' = 'thumbnail';
  public totalItemsInDatabase: number;

  public rootPath: string;
  public activatedRouteId: string;
  public selectedItemIds: string[] = [];
  public allSelectedInFilter: boolean = false;
  public excludedItemIds = new Set<string>();
  public hasUnauthorizedError: boolean = false;
  public isSearchInfoPopoverOpen: boolean = false;
  public searchInfoPopoverEvent?: Event;
  public isPublicWebApp = isPublicWebAppBuild();

  public get showRareDiseaseToggle(): boolean {
    return this.isPublicWebApp && !this.isModal;
  }

  public get showOnlyRareDiseases(): boolean {
    return this.queryParams.isRareDisease === true || this.queryParams.isRareDisease === 'true';
  }

  private topicService = inject(TopicService);

  public onItemClicked(item: TopicModel): Promise<boolean> {
    if (this.isModal) {
      this.toggleSelectedItem(item);

      return Promise.resolve(true);
    }

    if (this.isPublicWebApp) {
      return this.navigate([ORGANIZATION_LIST_ROUTE], {
        queryParams: { topics: item.id },
      }, 'root');
    }

    return this.navigate([item.id],
      {
        queryParams: this.queryParams,
        relativeTo: this.activatedRoute,
        state: {
          item,
        },
      },
      'root');
  }

  public dismiss(data?: any): void {
    this.modalController.dismiss(data).catch();
  }

  public submit(): void {
    const items = this.selectedItems.slice();

    if (!this.enableAllSelection) {
      this.dismiss(items);

      return;
    }

    this.dismiss({
      items,
      selection: {
        excludedIds: Array.from(this.excludedItemIds),
        isAllSelected: this.allSelectedInFilter,
        whereFilter: this.filters[this.currentContentId]?.where || {},
      },
    });
  }

  public toggleSelectedItem(item: TopicModel): void {
    if (this.allSelectedInFilter) {
      if (this.excludedItemIds.has(item.id)) {
        this.excludedItemIds.delete(item.id);
      } else {
        this.excludedItemIds.add(item.id);
      }

      this.syncAllSelectedItems();

      return;
    }

    if (!this.isMultiSelect) {
      this.selectedItems = [item];
      this.selectedItemIds = [item.id];

      return;
    }

    if (this.selectedItems.find((o) => { return o.id === item.id; })) {
      this.selectedItems = this.selectedItems.filter((o) => { return o.id !== item.id; });
    } else {
      this.selectedItems.push(item);
    }

    this.selectedItemIds = this.selectedItems.map((o) => { return o.id; });
  }

  public toggleSelectAllInFilter(): void {
    this.allSelectedInFilter = !this.allSelectedInFilter;
    this.excludedItemIds = new Set();
    if (this.allSelectedInFilter) {
      this.syncAllSelectedItems();
    } else {
      this.selectedItemIds = [];
      this.selectedItems = [];
    }
  }

  public onFilterButtonClicked(): void {
    this.openFilter(FORM_FILTER_KEYS, 'topic').catch();
  }

  public onRareDiseaseUpdated(event: Event): void {
    const showOnlyRareDiseases = (event as CustomEvent<{ checked?: boolean }>).detail?.checked === true;

    if (showOnlyRareDiseases === this.showOnlyRareDiseases) {
      return;
    }

    this.updateQueryParamsBySearchValue(showOnlyRareDiseases ? 'true' : '', 'isRareDisease');

    if (this.content) {
      this.content.scrollToTop().catch();
    }
  }

  public openSearchInfoPopover(event: Event): void {
    this.searchInfoPopoverEvent = event;
    this.isSearchInfoPopoverOpen = true;
  }

  // override base-list.page ngOnInit
  public ngOnInit(): void {
    if (this.isModal) {
      this.selectedItemIds = this.selectedItems.map((o) => { return o.id; });
      if (this.enableAllSelection && this.selectedAllState?.isAllSelected) {
        this.allSelectedInFilter = true;
        this.excludedItemIds = new Set(this.selectedAllState.excludedIds || []);
      }
    } else {
      this.rootPath = this.getRootPath(this.router.url, this.path);
      this.activatedRouteId = this.getDetailIdFromUrl(this.router.url, this.path);
    }

    this.listIds = this.isModal
      ? [`${this.path}-modal`]
      : [this.path];
    [this.currentContentId] = this.listIds;
    if (this.isModal && this.enableAllSelection && this.selectedAllState?.whereFilter
      && Object.keys(this.queryParams || {}).length === 0) {
      this.seedQueryParamsFromPersistedSelectionFilter();
    }
    this.initList(this.currentContentId);
  }

  protected subscribeToContent(): void {
    if (!this.isModal) {
      // observe detail route changes
      this.router.events
        .pipe(takeUntil(this.ngUnsubscribe),
          filter((event) => { return event instanceof NavigationEnd; }))
        .subscribe((ev: NavigationEnd) => {
          if (ev instanceof NavigationEnd && ev.url) {
            this.activatedRouteId = this.getDetailIdFromUrl(this.router.url, this.path);
          }
        });

      // observe search and filter changes
      //
      // queryParams (sanitized) should always reflect applied filter
      // currently we only do this for our where filter
      // params like order or offset are not part of query params yet
      this.activatedRoute.queryParams.subscribe((queryParams) => {
        this.queryParams = {
          ...queryParams,
        };

        this.onQueryParamsUpdated(this.currentContentId);
      });
    }

    this.subscribeToCurrentList(this.currentContentId);
  }

  protected hasMoreItems(id: string): boolean {
    return this.topicService.hasMoreItems(id);
  }

  protected async reloadContent(id: string): Promise<void> {
    try {
      this.setFilter(id, {
        skip: 0,
      });
      await lastValueFrom(this.topicService.loadContent(id, {
        filter: this.filters[id],
        type: 'list',
      }));
    } catch (error) {
      this.onRequestFailed(id, error);
    }
  }

  protected async loadNextItems(id: string): Promise<void> {
    try {
      this.setFilter(id, {
        skip: this.items[id].length,
      });
      await lastValueFrom(this.topicService.loadContent(id, {
        filter: this.filters[id],
        type: 'list',
      }));
    } catch (error) {
      this.onRequestFailed(id, error);
    }
  }

  protected initList(id: string): void {
    this.viewStates[id] = this.getInitialViewStates(false);
    this.items[id] = Array(SKELETON_ITEM_COUNT).fill(null);
  }

  protected onQueryParamsUpdated(id: string): void {
    const filterKeys = this.showRareDiseaseToggle ? PUBLIC_TOPIC_FILTER_KEYS : FORM_FILTER_KEYS;
    const where = this.getWhereFilterByQueryParams(filterKeys);
    const multiFieldSearchValue = this.getNormalizedSearchValue(this.queryParams[MULTI_FIELD_SEARCH_KEY]);

    if (multiFieldSearchValue) {
      where[MULTI_FIELD_SEARCH_KEY] = multiFieldSearchValue;
    }

    for (const key of Object.keys(this.queryParams)) {
      if (filterKeys.includes(key) && ARRAY_VALUES.includes(key as ArrayKeys<TopicForm>)) {
        const contains = this.queryParams[key].split(CONTAIN_FILTER_SEPARATOR);

        where[key] = {
          contains,
        };
      }
    }

    for (const key of ['icd10Codes', 'icd11Codes']) {
      if (!filterKeys.includes(key)) {
        continue;
      }

      const code = this.getNormalizedSearchValue(this.queryParams[key]);

      if (code) {
        where[key] = {
          search: code,
        };
      } else {
        delete where[key];
      }
    }

    const currentFilter: ClientFilterObject<TopicSource> = {
      order: {},
      where,
    };

    this.applyFilterSettings(id, currentFilter, this.topicService.getMetaData(id), filterKeys);
  }

  protected onRequestSucceeded(id: string): void {
    this.hasUnauthorizedError = false;
    super.onRequestSucceeded(id);
  }

  protected onRequestFailed(id: string, error: Partial<HttpErrorResponse>): void {
    this.hasUnauthorizedError = error?.status === HTTP_UNAUTHORIZED;
    super.onRequestFailed(id, error);
  }

  private subscribeToCurrentList(id: string): void {
    // when this.isModal = true
    // + just in case queryParams subscription wasn't initially triggered for any reason
    if (!this.filters[id]) {
      this.onQueryParamsUpdated(id);
    }

    this.topicService.getContent(id, {
      filter: this.filters[id],
      // reload content if list is opened as modal -> fix modal opening to slow or showing last searched items
      forceReload: this.isModal,
      type: 'list',
    })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((items): void => {
        if (items) {
          this.items[id] = items;
          this.totalItemsInDatabase = this.topicService.getMetaData(id).totalCount;
          if (this.allSelectedInFilter) {
            this.syncAllSelectedItems();
          }

          this.onRequestSucceeded(id);
        } else {
          const metadata = this.topicService.getMetaData(id);

          this.onRequestFailed(id, metadata.error);
        }
      });
  }

  private syncAllSelectedItems(): void {
    this.selectedItems = (this.items[this.currentContentId] || []).filter((item) => {
      return !!item?.id && !this.excludedItemIds.has(item.id);
    });
    this.selectedItemIds = this.selectedItems.map((item) => { return item.id; });
  }

  private seedQueryParamsFromPersistedSelectionFilter(): void {
    const where = this.selectedAllState?.whereFilter || {};
    const search = this.getNormalizedSearchValue(where[MULTI_FIELD_SEARCH_KEY]);

    if (search) {
      this.queryParams[MULTI_FIELD_SEARCH_KEY] = search;
    }

    for (const key of FORM_FILTER_KEYS) {
      const value = where[key as keyof TopicSource] as any;
      const param = value?.contains || value?.search || value;
      const queryValue = this.getQueryParamValue(param);

      if (queryValue !== null) {
        this.queryParams[key as string] = queryValue;
      }
    }
  }

  private getRootPath(route: string, subPath: string): string {
    const [normalizedRoute] = route.split('?');
    const subPathIndex = route.indexOf(subPath);

    if (subPathIndex === -1) {
      console.warn('subPath missing in ', route, subPath);

      return normalizedRoute;
    }

    return normalizedRoute.substring(0, subPathIndex + subPath.length);
  }

  private getDetailIdFromUrl(url: string, subPath: string): string {
    const [normalizedUrl] = url.split('?');
    const parts = normalizedUrl.split('/');
    const index = parts.indexOf(subPath);

    if (index === -1) {
      return '';
    }

    return parts[index + 1];
  }
}
